import React, { useEffect } from 'react'
import clsx from 'clsx'
import { ZinIcon } from '@tnl/zin-react'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { MkConfig } from '@typescript'
import { configs } from '@configs'
import { useModal } from '@hooks/useModal'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useMeContext } from '@contexts/MeContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { IMkAuthorizedSession } from '@auth/types'
import { useLogout } from '@hooks/useLogout'
import { ACCOUNT_MENU_USE_BY_LABEL, DEFAULT_CONTRACT_LABEL, SCROLL_THRESHOLD } from '@constants'
import { formatPointsWithCommas, toTitleCase } from '@utils/formattingUtils'
import { formatDateForUI } from '@utils/formatDateForUI'
import { toContractDetails } from '@shared/utils/membershipUtils'
import { MkAccountMenuWidgetProps } from './MkAccountMenuWidget'
import { MkAccountMenuItem } from './MkAccountMenuItems'
import { UserInitialsAvatar } from '@shared/components/UserInitialsAvatar/UserInitialsAvatar'
import './MkAccountMenu.scss'
import { stripBase, withBase } from '@shared/utils/basePath'
import { AccountMenuHookResult } from './types'

const ACCOUNT_MENU_POINTS_LABEL = 'points'
const ACCOUNT_MENU_CREDITS_LABEL = 'credits'

function isPointsBrand(brand: string | undefined): boolean {
  const b = (brand || '').toLowerCase()
  return b === 'sir' || b === 'cw'
}

/**
 * Manages account menu state and config: consumes shared /me data, builds header from
 * user/memberships, and menu items from linkGroups (with logout last and base path handling).
 */
export const useAccountMenu = ({
  onMenuToggled = () => {},
  onCloseMenu,
}: {
  kind: string
  resourceKey: string
  menuItemConfig: MkConfig
  onMenuToggled?: (state: boolean) => void
  onCloseMenu?: () => void
}): AccountMenuHookResult => {
  const { isAuthorized: isLoggedIn, session } = useAuthentication()
  const { handleLogout } = useLogout()
  const navigate = useNavigate()
  const { switchTripsPage } = useFlags()
  const { themeBrand } = useSiteContext()
  const { me: accountMenu } = useMeContext()
  const pointsLabel = isPointsBrand(themeBrand) ? ACCOUNT_MENU_POINTS_LABEL : ACCOUNT_MENU_CREDITS_LABEL
  const isEbac = themeBrand === 'ebac'

  const { openSignInModal, openRegisterModal } = useModal({
    modalID: 'account-menu',
    config: { ...configs.primereact.dialog },
  })

  const [isMenuOpen, setIsMenuOpen] = React.useState(false)

  const closeAccountMenu = React.useCallback(() => {
    setIsMenuOpen(false)
    onCloseMenu?.()
  }, [onCloseMenu])

  const toggleAccountMenu = React.useCallback(() => {
    setIsMenuOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    onMenuToggled(isMenuOpen)
  }, [isMenuOpen, onMenuToggled])

  // Close menu when scrolling to threshold
  useEffect(() => {
    const handleScroll = () => {
      if (isMenuOpen && window.scrollY > SCROLL_THRESHOLD) {
        closeAccountMenu()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMenuOpen, closeAccountMenu])

  const user = React.useMemo(() => {
    if (isLoggedIn && session) {
      return {
        ...(session as IMkAuthorizedSession)?.authorizedUser,
      }
    }
    return null
  }, [isLoggedIn, session])

  // Widget credits/use-by should reflect the currently authorized membership.
  const selectedMembershipForWidget = React.useMemo(
    () =>
      Array.isArray(accountMenu?.user?.memberships) && accountMenu.user.memberships.length > 0
        ? accountMenu.user.memberships.find((membership) => membership.id === user?.memberNumber) ?? accountMenu.user.memberships[0]
        : null,
    [accountMenu?.user?.memberships, user?.memberNumber],
  )
  const displayCreditsForWidget = React.useMemo(() => {
    if (selectedMembershipForWidget?.credits?.balance != null) return String(selectedMembershipForWidget.credits.balance)
    return '0'
  }, [selectedMembershipForWidget?.credits?.balance])
  const displayUseByForWidget = React.useMemo(
    () => formatDateForUI(selectedMembershipForWidget?.credits?.useBy ?? null),
    [selectedMembershipForWidget?.credits?.useBy],
  )

  const widgetButtonConfigs: MkAccountMenuWidgetProps['buttonConfigs'] = React.useMemo(
    () => [
      // Show points button when logged in and /me has been loaded (so we have membership credits)
      ...(isLoggedIn && accountMenu != null
        ? [
            {
              key: 'account-widget-points',
              itemClassName: clsx({ hidden: !isLoggedIn }),
              label: 'points',
              onClick: toggleAccountMenu,
              ptConfig: { root: () => ({ className: 'account-menu__widget__points-btn' }) },
              children: [
                <span
                  key="account-widget-point-text"
                  className={clsx('account-menu__widget__points-text subtitle-2')}
                  data-testid="available-credits">
                  {`${formatPointsWithCommas(displayCreditsForWidget)} ${isEbac ? pointsLabel.toUpperCase() : pointsLabel}`}
                </span>,
                ...(!isEbac && displayUseByForWidget
                  ? [
                      <span
                        key="account-widget-points-use-by"
                        data-testid="account-widget-points-use-by"
                        className={clsx('account-menu__widget__points-btn__use-by body-2')}
                        style={{ color: 'var(--zin-header-menu-link-primary-text)' }}>
                        {`${ACCOUNT_MENU_USE_BY_LABEL} ${displayUseByForWidget}`}
                      </span>,
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        key: 'account-widget-avatar',
        itemClassName: clsx({ hidden: !isLoggedIn }),
        label: 'avatar',
        onClick: toggleAccountMenu,
        ptConfig: { root: () => ({ className: 'account-menu__widget__avatar-btn' }) },
        children: [
          <UserInitialsAvatar
            key="account-widget-avatar-content"
            variant="header"
            firstName={user?.first}
            lastName={user?.last}
          />,
        ],
      },
      {
        key: 'account-widget-register',
        itemClassName: clsx('mk-registerButton', { hidden: isLoggedIn }),
        label: 'Register',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onClick: (_: any) => openRegisterModal(),
        ptConfig: {
          root: ({ props }: any) => ({
            className: clsx('h-20 px-6 py-2 justify-start items-center gap-1 hidden md:inline-flex', { 'rounded-md': !props.rounded, 'rounded-full': props.rounded }),
          }),
          label: () => ({ className: clsx('text-center text-base font-semibold font-["Poppins"] leading-normal') }),
          icon: () => ({ className: clsx('mx-0') }),
        },
      },
      {
        key: 'account-widget-signIn',
        itemClassName: clsx('mk-signInButton', { hidden: isLoggedIn }),
        label: 'Sign In',
        icon: (
          <ZinIcon
            className="w-8"
            name="circle-user"
            set="fal"
          />
        ),
        'data-testid': 'sign-in-button',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onClick: (_: any) => openSignInModal(),
        ptConfig: {
          root: () => ({
            className: clsx(
              'inline-flex items-center mt-[0] mx-[0] px-4 py-[.85rem] border-[1px] border-solid border-[transparent] rounded-[4px] [transition:background-color_.25s_ease-out,_color_.25s_ease-out] [font-family:inherit] text-[.9rem] leading-none text-center cursor-pointer',
            ),
          }),
          label: () => ({ className: 'text-center text-base font-semibold font-["Poppins"] leading-normal hidden md:inline' }),
          icon: () => ({ className: 'flex-shrink-0' }),
        },
      },
    ],
    [isLoggedIn, user, toggleAccountMenu, openSignInModal, openRegisterModal, accountMenu, displayCreditsForWidget, displayUseByForWidget, pointsLabel],
  )

  const {
    headerTitle,
    headerSubtitle,
    available,
    total,
    contractDetails,
  } = React.useMemo(() => {
    const meUser = accountMenu?.user
    const memberships = meUser?.memberships

    // Contract/membership details from /me only
    const contractDetailsData = toContractDetails(memberships)

    const meFirst = meUser?.name?.first
    const meLast = meUser?.name?.last
    const headerTitleValue =
      meFirst != null && meLast != null
        ? `${toTitleCase(meFirst)} ${toTitleCase(meLast)}`
        : user?.first && user?.last
          ? `${toTitleCase(user.first)} ${toTitleCase(user.last)}`
          : 'Account'

    const selectedMembership = Array.isArray(memberships) && memberships.length > 0 ? memberships.find((membership) => membership.id === user?.memberNumber) ?? memberships[0] : null
    const availableValue = selectedMembership?.credits?.balance != null ? Number(selectedMembership.credits.balance) : 0
    const subtitleFromMe = selectedMembership?.level?.label ?? null

    return {
      headerTitle: headerTitleValue,
      headerSubtitle: subtitleFromMe ?? DEFAULT_CONTRACT_LABEL,
      available: availableValue,
      total: 0,
      contractDetails: contractDetailsData,
    }
  }, [accountMenu?.user, user])

  const menuItems = React.useMemo<MkAccountMenuItem[]>(() => {
    if (accountMenu === null) return []

    const linkGroups = accountMenu.linkGroups
    if (!Array.isArray(linkGroups) || linkGroups.length < 1) return []

    const mainGroup = linkGroups[0]
    const mainLinks = Array.isArray(mainGroup?.links) ? mainGroup.links : []
    const tripsPage = '/account/my-trips'

    const mainFiltered = mainLinks.filter(({ url }) => url !== tripsPage || (url === tripsPage && switchTripsPage))

    // Logout comes from the separate second group (linkGroups[1]) when present; it already has the icon
    const logoutGroup = linkGroups[1]
    const logoutGroupLinks = Array.isArray(logoutGroup?.links) ? logoutGroup.links : []
    const logoutLink = logoutGroupLinks.find(({ label }) => (label || '').toLowerCase().trim() === 'log out') ?? null

    const itemsToRender = logoutLink ? [...mainFiltered, logoutLink] : mainFiltered

    return itemsToRender.map(({ label, url, icon }) => {
      const isLogOut = (label || '').toLowerCase().trim() === 'log out'
      const toPath = stripBase(url || '')
      const stableKey = `${toPath}-${(label || '').trim()}`
      return {
        key: stableKey,
        label,
        link: isLogOut ? undefined : withBase(toPath),
        icon: icon?.name ? (
          <ZinIcon
            name={icon.name}
            {...(isLogOut && { set: 'fas' })}
          />
        ) : isLogOut ? (
          <ZinIcon
            name="arrow-right-to-bracket"
            set="fas"
          />
        ) : undefined,
        onClick: isLogOut
          ? async () => {
              await handleLogout()
              toggleAccountMenu()
            }
          : () => {
              navigate(toPath)
              toggleAccountMenu()
            },
        ptConfig: {
          root: () => ({ className: 'justify-start items-center gap-1' }),
        },
      }
    })
  }, [accountMenu, navigate, toggleAccountMenu, switchTripsPage, handleLogout])

  return {
    widgetButtonConfigs,
    isMenuOpen,
    toggleAccountMenu,
    closeAccountMenu,
    headerTitle,
    headerSubtitle,
    available,
    total,
    contractDetails,
    menuItems,
  }
}
